import { useState } from 'react';
import { Box, Container, Grid, Typography, Paper, TextField, Button, Alert } from '@mui/material';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import toast from 'react-hot-toast';

export function ContactSection({ data }) {
  const primaryColor = data.primary_color || '#1e3a8a';
  const contact = data.contact;

  if (!contact || typeof contact !== 'object') return null;

  const email = contact.email || null;
  const phone = contact.phone || null;
  const address = contact.address || null;
  const mapEmbed = contact.google_map_embed || null;

  if (!email && !phone && !address && !mapEmbed) return null;

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitted(true);
    toast.success('Thank you for reaching out! Our admissions desk will contact you soon.');
    setForm({ name: '', email: '', phone: '', message: '' });
  };

  return (
    <Box id="contact" sx={{ py: { xs: 8, md: 12 }, backgroundColor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: 7 }}>
          <Box sx={{ display: 'inline-block', px: 2, py: 0.5, borderRadius: 4, backgroundColor: `${primaryColor}15`, color: primaryColor, mb: 1.5 }}>
            <Typography variant="caption" fontWeight={700} letterSpacing="0.05em">
              GET IN TOUCH
            </Typography>
          </Box>
          <Typography variant="h2" fontWeight={800} color="#0f172a" sx={{ fontSize: { xs: '2rem', md: '2.5rem' }, mb: 1 }}>
            Contact & Location
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Have questions about admissions or school visits? We are here to assist you.
          </Typography>
        </Box>

        <Grid container spacing={5}>
          {/* Left Column: Contact Cards + Form */}
          <Grid size={{ xs: 12, md: mapEmbed ? 6 : 12 }}>
            <Box sx={{ mb: 4 }}>
              <Grid container spacing={2}>
                {address && (
                  <Grid size={{ xs: 12 }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: `${primaryColor}15`, color: primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <MapPin size={22} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Address</Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a">{address}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {phone && (
                  <Grid size={{ xs: 12, sm: email ? 6 : 12 }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: `${primaryColor}15`, color: primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Phone size={20} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Phone</Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a">{phone}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                )}

                {email && (
                  <Grid size={{ xs: 12, sm: phone ? 6 : 12 }}>
                    <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: 2, backgroundColor: `${primaryColor}15`, color: primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Mail size={20} />
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary" fontWeight={600}>Email</Typography>
                        <Typography variant="subtitle2" fontWeight={700} color="#0f172a">{email}</Typography>
                      </Box>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Box>

            {/* Inquiry Form */}
            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0' }}>
              <Typography variant="h6" fontWeight={800} color="#0f172a" sx={{ mb: 3 }}>
                Send Admission Inquiry
              </Typography>

              {submitted && (
                <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                  Inquiry submitted successfully!
                </Alert>
              )}

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Parent Name"
                      size="small"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      size="small"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      size="small"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={3}
                      label="Message / Query"
                      size="small"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid size={{ xs: 12 }}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      startIcon={<Send size={18} />}
                      sx={{
                        backgroundColor: primaryColor,
                        py: 1.2,
                        fontWeight: 700,
                        textTransform: 'none',
                      }}
                    >
                      Submit Inquiry
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          {/* Right Column: Google Map */}
          {mapEmbed && (
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  minHeight: 400,
                  borderRadius: 4,
                  overflow: 'hidden',
                  boxShadow: '0 15px 35px rgba(0,0,0,0.08)',
                  border: '1px solid #e2e8f0',
                }}
              >
                <iframe
                  src={mapEmbed}
                  title="School Location Map"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: 400 }}
                  allowFullScreen=""
                  loading="lazy"
                />
              </Box>
            </Grid>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default ContactSection;
